/**
 * VerifyFunnelBar — docked under the sticky header while a verify-next walk
 * is running. Drop-filtered walks (E3/E8) say "this drop is done" at zero;
 * they do not claim a launch gate.
 */
import { useState } from 'react';
import { message } from 'antd';
import { sectionForEntry, BB_LETTER_TO_SECTION, type BbLetter } from '@/lib/bbSectionMap';
import { bbEntryKindLabel } from '@/components/brand-bible/bbEntryLabel';
import type { AttentionRow } from '@/components/brand-bible/NeedsAttentionQueue';
import type { VerifyNextFunnelState } from './useVerifyNextFunnel';
import { isVoiceReviewRow } from './dropReview';

function letterFor(row: AttentionRow | null): BbLetter | null {
  if (!row) return null;
  const section = sectionForEntry(row.table, row.entry);
  if (!section) return null;
  return (Object.keys(BB_LETTER_TO_SECTION) as BbLetter[]).find((l) => BB_LETTER_TO_SECTION[l] === section) ?? null;
}

function labelFor(row: AttentionRow | null): string {
  return row ? bbEntryKindLabel(row.table, row.entry) : '';
}

export interface VerifyFunnelBarProps {
  funnel: VerifyNextFunnelState;
  /** 'drop' (default) = this-drop copy. 'all' keeps the generic walk. */
  scope?: 'drop' | 'all';
  onDone?: () => void;
}

export function VerifyFunnelBar({ funnel, scope = 'drop', onDone }: VerifyFunnelBarProps) {
  const { phase } = funnel;
  const [busy, setBusy] = useState<'verify' | 'reject' | null>(null);
  if (phase === 'idle') return null;

  const run = async (kind: 'verify' | 'reject', fn: () => Promise<void>) => {
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      message.error(`${kind === 'reject' ? 'Reject' : 'Verify'} failed — ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      setBusy(null);
    }
  };

  const dismiss = () => {
    funnel.stop();
    onDone?.();
  };

  if (phase === 'done') {
    return (
      <div
        role="status"
        data-testid="verify-funnel-bar"
        data-phase="done"
        className="flex items-center gap-4 border-b border-[hsl(var(--status-success)/0.3)] bg-[hsl(var(--status-success-bg))] px-8 py-2.5 text-[13.5px] text-[hsl(var(--status-success))]"
      >
        <b className="font-bold">
          {scope === 'drop'
            ? 'This drop is done'
            : `All ${funnel.originalCount ?? 0} verified`}
        </b>
        <button
          type="button"
          onClick={dismiss}
          className="ml-auto rounded-md border border-[hsl(var(--status-success)/0.3)] bg-card px-3 py-1 text-[13px] font-semibold text-[hsl(var(--status-success))] cursor-pointer"
        >
          Done
        </button>
      </div>
    );
  }

  if (phase === 'precommit') {
    const voice = isVoiceReviewRow(funnel.focused);
    const nextAfterLabel = labelFor(funnel.upcoming);
    return (
      <div
        role="status"
        data-testid="verify-funnel-bar"
        data-phase="precommit"
        className="flex items-center gap-4 border-b border-coral/20 bg-coral-light px-8 py-2.5 text-[13.5px] text-coral-dark"
      >
        <span>
          <b className="font-bold">{voice ? 'Does this sound like you?' : 'Verify this item ↓'}</b>
          {nextAfterLabel ? <> · next after {nextAfterLabel}</> : null}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={!funnel.focused || busy !== null}
            onClick={() => void run('verify', funnel.commitFocused)}
            className="rounded-md bg-coral px-3 py-1 text-[13px] font-semibold text-white hover:bg-coral-hover disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {busy === 'verify' ? 'Verifying…' : '✓ Verify'}
          </button>
          <button
            type="button"
            disabled={!funnel.focused || busy !== null}
            onClick={() => void run('reject', funnel.rejectFocused)}
            className="rounded-md border border-coral/25 bg-card px-3 py-1 text-[13px] font-semibold text-teal cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy === 'reject' ? 'Rejecting…' : 'Reject'}
          </button>
          <button
            type="button"
            onClick={funnel.stop}
            className="rounded-md border border-coral/25 bg-card px-3 py-1 text-[13px] font-semibold text-teal cursor-pointer"
          >
            Stop
          </button>
        </div>
      </div>
    );
  }

  const nextRow = funnel.upcoming;
  const nextLetter = letterFor(nextRow);
  const nextLabel = labelFor(nextRow);
  const remaining = (funnel.originalCount ?? funnel.verifiedCount) - funnel.verifiedCount;

  return (
    <div
      role="status"
      data-testid="verify-funnel-bar"
      data-phase="postcommit"
      className="flex items-center gap-4 border-b border-coral/20 bg-coral-light px-8 py-2.5 text-[13.5px] text-coral-dark"
    >
      <b className="font-bold">
        {remaining} of {funnel.originalCount ?? remaining} left
      </b>
      {nextRow ? (
        <span className="text-teal">
          Next: {nextLabel} in §{nextLetter}
        </span>
      ) : (
        <span className="text-teal">All caught up this pass</span>
      )}
      <button
        type="button"
        onClick={funnel.verifyNext}
        disabled={!nextRow}
        className="font-semibold underline decoration-coral/50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
      >
        Verify next →
      </button>
      <button
        type="button"
        onClick={funnel.skip}
        disabled={!nextRow}
        className="text-teal disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
      >
        Skip
      </button>
      <button
        type="button"
        onClick={funnel.stop}
        className="ml-auto rounded-md border border-coral/25 bg-card px-3 py-1 text-[13px] font-semibold text-teal cursor-pointer"
      >
        Stop
      </button>
    </div>
  );
}
